import { cloneDeep } from 'lodash';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
  formatStoreAlias, HMStoISO, parseSLSandBrickworksErrors,
} from '../../utils/formatting';
import { loadState } from '../../utils/local-storage';
import { sfsPost, sfsPut } from '../../utils/service-calls/o2o-capabilities';
import { putStore } from '../../utils/service-calls/sls';
import { daysOfTheWeek } from '../o2o-capabilities/sfs/constants';

import {
  buildBopisPutBody,
  buildFailureRecord,
  buildSfsCapacitiesBody,
  buildSfsStoreServicesPutBody,
  chunkArray,
  DEFAULT_CHUNK_SIZE,
  deriveBopisFormValues,
  deriveSfsFormValues,
  isBopisRowDirty,
  isBopisRowValid,
  isSfsRowDirty,
  isSfsRowValid,
} from './helpers';
import Step1SelectStores from './Step1SelectStores';
import Step2ConfigureAndSubmit from './Step2ConfigureAndSubmit';
import Stepper from './Stepper';

import './bulkupdate.css';

const initialProgress = () => ({
  failureCount: 0, processedCount: 0, successCount: 0, total: 0,
});

// auth.country is already stored as { label, value } by components/Authorized.js
const initialCountryFromAuth = () => loadState('auth')?.country || null;

const BulkUpdate = () => {
  const accessToken = useSelector((state) => state.authorizationReducer.auth.accessToken);

  const [currentStep, setCurrentStep] = useState(1);
  const [fulfillmentOption, setFulfillmentOption] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(initialCountryFromAuth);
  const [selectedStores, setSelectedStores] = useState([]);
  const [currentValuesCache, setCurrentValuesCache] = useState({});
  const [formValuesByStore, setFormValuesByStore] = useState({});
  const [submitState, setSubmitState] = useState('idle');
  const [progress, setProgress] = useState(initialProgress);
  const [failures, setFailures] = useState([]);
  const [failuresDownloaded, setFailuresDownloaded] = useState(false);
  const [inProgressChunk, setInProgressChunk] = useState([]);

  const fulfillmentType = fulfillmentOption ? fulfillmentOption.value : null;
  const hasCachedValues = Object.keys(currentValuesCache).length > 0;

  // beforeunload guard while running
  useEffect(() => {
    if (submitState !== 'running') return undefined;
    const handler = (e) => {
      e.preventDefault();
      // eslint-disable-next-line no-param-reassign
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [submitState]);

  // Whenever the cache changes, seed formValuesByStore[storeId] for any newly
  // cached store. We do NOT overwrite an entry that already exists - the user's
  // in-flight edits must survive cache refreshes during Step1 <-> Step2 navigation.
  useEffect(() => {
    if (!fulfillmentType) return;
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      Object.keys(currentValuesCache).forEach((storeId) => {
        const cached = currentValuesCache[storeId];
        if (cached.fetchError) return;
        if (next[storeId]) return;
        next[storeId] = fulfillmentType === 'BOPIS'
          ? deriveBopisFormValues(cached)
          : deriveSfsFormValues(cached);
      });
      return next;
    });
  }, [currentValuesCache, fulfillmentType]);

  // Auto return to Step 1 if user removes all stores while on Step 2.
  useEffect(() => {
    if (currentStep === 2 && selectedStores.length === 0 && submitState === 'idle') {
      setCurrentStep(1);
    }
  }, [currentStep, selectedStores, submitState]);

  const onChangeFulfillmentOption = (option) => {
    setFulfillmentOption(option);
    setCurrentValuesCache({});
    setFormValuesByStore({});
  };

  const onChangeCountry = (option) => {
    setSelectedCountry(option);
    setSelectedStores([]);
    setCurrentValuesCache({});
    setFormValuesByStore({});
  };

  const onChangeSelectedStores = (stores) => {
    setSelectedStores(stores);
    const allowed = new Set(stores.map((s) => s.id));
    setCurrentValuesCache((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { if (allowed.has(key)) next[key] = prev[key]; });
      return next;
    });
    setFormValuesByStore((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { if (allowed.has(key)) next[key] = prev[key]; });
      return next;
    });
  };

  const onCacheStore = (storeId, row) => {
    setCurrentValuesCache((prev) => ({ ...prev, [storeId]: { ...prev[storeId], ...row } }));
  };

  const onPrefetchError = (storeId, message) => {
    setCurrentValuesCache((prev) => ({ ...prev, [storeId]: { fetchError: message } }));
  };

  const onRemoveStore = (storeId) => {
    setSelectedStores((prev) => prev.filter((s) => s.id !== storeId));
    setCurrentValuesCache((prev) => {
      const next = { ...prev };
      delete next[storeId];
      return next;
    });
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      delete next[storeId];
      return next;
    });
  };

  // Per-row edit handlers
  const onUpdateRowValues = (storeId, patch) => {
    setFormValuesByStore((prev) => {
      const current = prev[storeId];
      if (!current) return prev;
      return { ...prev, [storeId]: { ...current, ...patch } };
    });
  };

  const onResetRow = (storeId) => {
    const cached = currentValuesCache[storeId];
    if (!cached) return;
    const reset = fulfillmentType === 'BOPIS'
      ? deriveBopisFormValues(cached)
      : deriveSfsFormValues(cached);
    setFormValuesByStore((prev) => ({ ...prev, [storeId]: reset }));
  };

  // Apply-to-all handlers. `storeIds` is optional; when omitted the broadcast
  // hits every entry in formValuesByStore. When provided (from a filtered
  // Step 2 view) it limits the broadcast to visible rows only.
  const targetKeys = (allKeys, storeIds) => {
    if (!storeIds) return allKeys;
    const set = new Set(storeIds);
    return allKeys.filter((k) => set.has(k));
  };

  const onApplyEnabledToAll = (value, storeIds) => {
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      targetKeys(Object.keys(next), storeIds).forEach((storeId) => {
        next[storeId] = { ...next[storeId], enabled: value };
      });
      return next;
    });
  };

  const onApplyLeadTimeToAll = (leadTimeData, storeIds) => {
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      targetKeys(Object.keys(next), storeIds).forEach((storeId) => {
        next[storeId] = { ...next[storeId], leadTimeData: { ...leadTimeData } };
      });
      return next;
    });
  };

  const onApplyDayToAll = (day, maxUnits, storeIds) => {
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      targetKeys(Object.keys(next), storeIds).forEach((storeId) => {
        const row = next[storeId];
        if (!row || !row.defaultCapacity) return;
        next[storeId] = {
          ...row,
          defaultCapacity: {
            ...row.defaultCapacity,
            [day]: { maxUnits },
          },
        };
      });
      return next;
    });
  };

  const onApplyExceptionsToAll = (exceptions, storeIds) => {
    setFormValuesByStore((prev) => {
      const next = { ...prev };
      targetKeys(Object.keys(next), storeIds).forEach((storeId) => {
        next[storeId] = {
          ...next[storeId],
          capacityExceptions: cloneDeep(exceptions || []),
        };
      });
      return next;
    });
  };

  const onPrevious = () => setCurrentStep(1);
  const onNext = () => setCurrentStep(2);
  const onRequestSubmit = () => setSubmitState('confirming');
  const onCancelSubmit = () => setSubmitState('idle');

  // Dirty / valid computations derived from current state
  const dirtyStoreIds = useMemo(() => {
    if (!fulfillmentType) return [];
    return selectedStores
      .filter((store) => {
        const current = formValuesByStore[store.id];
        const cached = currentValuesCache[store.id];
        if (!current || !cached || cached.fetchError) return false;
        return fulfillmentType === 'BOPIS'
          ? isBopisRowDirty(current, cached)
          : isSfsRowDirty(current, cached);
      })
      .map((store) => store.id);
  }, [selectedStores, formValuesByStore, currentValuesCache, fulfillmentType]);

  const allRowsValid = useMemo(() => {
    if (!fulfillmentType) return false;
    return selectedStores.every((store) => {
      const current = formValuesByStore[store.id];
      const cached = currentValuesCache[store.id];
      if (cached && cached.fetchError) return false;
      if (!current) return true; // still loading
      return fulfillmentType === 'BOPIS'
        ? isBopisRowValid(current)
        : isSfsRowValid(current);
    });
  }, [selectedStores, formValuesByStore, currentValuesCache, fulfillmentType]);

  // Refs we use during the running fan-out
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const finishOne = (success, failureRecord) => {
    setProgress((prev) => ({
      ...prev,
      failureCount: prev.failureCount + (success ? 0 : 1),
      processedCount: prev.processedCount + 1,
      successCount: prev.successCount + (success ? 1 : 0),
    }));
    if (!success && failureRecord) {
      setFailures((prev) => [...prev, failureRecord]);
    }
  };

  const runBopisOne = async (store) => {
    const cached = currentValuesCache[store.id];
    const current = formValuesByStore[store.id];
    if (!cached || !cached.rawStore || !current) {
      finishOne(false, buildFailureRecord(store, 'Missing cached store data'));
      return;
    }
    const { enabled, leadTimeData } = current;
    const leadTimeISO = HMStoISO(leadTimeData.hours, leadTimeData.minutes, leadTimeData.seconds);
    const body = buildBopisPutBody(cached.rawStore, { enabled, leadTimeISO });
    try {
      await putStore(accessToken, body);
      finishOne(true);
    } catch (err) {
      const parsed = parseSLSandBrickworksErrors(err, true);
      if (parsed.brickworks) {
        finishOne(true);
      } else if (parsed.timeout) {
        finishOne(false, buildFailureRecord(store, 'SLS PUT timed out'));
      } else {
        finishOne(false, buildFailureRecord(store, `SLS PUT failed: ${parsed.message || err.message || 'Unknown error'}`));
      }
    }
  };

  const runSfsOne = async (store) => {
    const cached = currentValuesCache[store.id];
    const current = formValuesByStore[store.id];
    if (!cached || !cached.rawStore || !current) {
      finishOne(false, buildFailureRecord(store, 'Missing cached store data'));
      return;
    }
    const { enabled, defaultCapacity, capacityExceptions } = current;
    const storeServicesPayload = buildSfsStoreServicesPutBody(cached.rawStore, { enabled });
    const capacitiesPayload = buildSfsCapacitiesBody({
      capacityExceptions,
      defaultCapacity,
      existing: cached.sfs,
    });

    const slsCall = putStore(accessToken, storeServicesPayload)
      .then(() => ({ ok: true }))
      .catch((err) => {
        const parsed = parseSLSandBrickworksErrors(err, true);
        if (parsed.brickworks) return { ok: true };
        if (parsed.timeout) return { message: 'SLS PUT timed out', ok: false };
        return { message: `SLS PUT failed: ${parsed.message || err.message || 'Unknown error'}`, ok: false };
      });

    const sfsCall = (cached.sfs && cached.sfs.id
      ? sfsPut(accessToken, store.id, capacitiesPayload, cached.sfs.id)
      : sfsPost(accessToken, store.id, capacitiesPayload))
      .then(() => ({ ok: true }))
      .catch((err) => ({ message: `Fulfillment Capacities failed: ${err.message || 'Unknown error'}`, ok: false }));

    const [slsResult, sfsResult] = await Promise.all([slsCall, sfsCall]);
    if (slsResult.ok && sfsResult.ok) {
      finishOne(true);
    } else {
      const messages = [];
      if (!slsResult.ok) messages.push(slsResult.message);
      if (!sfsResult.ok) messages.push(sfsResult.message);
      finishOne(false, buildFailureRecord(store, messages.join('; ')));
    }
  };

  const runFanOut = async (storesToSubmit) => {
    const runner = fulfillmentType === 'BOPIS' ? runBopisOne : runSfsOne;
    const chunks = chunkArray(storesToSubmit, DEFAULT_CHUNK_SIZE);
    // eslint-disable-next-line no-restricted-syntax
    for (const chunk of chunks) {
      if (!isMountedRef.current) return;
      setInProgressChunk(chunk.map(formatStoreAlias));
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(chunk.map(runner));
    }
    setInProgressChunk([]);
    if (isMountedRef.current) setSubmitState('done');
  };

  const onConfirmSubmit = () => {
    const dirtySet = new Set(dirtyStoreIds);
    const storesToSubmit = selectedStores.filter((s) => dirtySet.has(s.id));
    if (storesToSubmit.length === 0) {
      setSubmitState('idle');
      return;
    }
    setProgress({
      failureCount: 0, processedCount: 0, successCount: 0, total: storesToSubmit.length,
    });
    setFailures([]);
    setFailuresDownloaded(false);
    setSubmitState('running');
    // Defer the fan-out so React applies the running state before requests fire.
    setTimeout(() => { runFanOut(storesToSubmit); }, 0);
  };

  const onDownloadFailures = () => setFailuresDownloaded(true);

  const onStartOver = () => {
    setCurrentStep(1);
    setSelectedStores([]);
    setCurrentValuesCache({});
    setFormValuesByStore({});
    setSubmitState('idle');
    setProgress(initialProgress());
    setFailures([]);
    setFailuresDownloaded(false);
    setInProgressChunk([]);
  };

  const onRetryFailed = () => {
    const failedIds = new Set(failures.map((f) => f.storeId));
    const remaining = selectedStores.filter((s) => failedIds.has(s.id));
    setSelectedStores(remaining);
    setCurrentValuesCache((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { if (failedIds.has(key)) next[key] = prev[key]; });
      return next;
    });
    setFormValuesByStore((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { if (failedIds.has(key)) next[key] = prev[key]; });
      return next;
    });
    setSubmitState('idle');
    setProgress(initialProgress());
    setFailures([]);
    setFailuresDownloaded(false);
    setInProgressChunk([]);
  };

  const stepperLocked = submitState === 'running'
    || (submitState === 'done' && failures.length > 0 && !failuresDownloaded);

  const navigateStep = (step) => {
    if (stepperLocked) return;
    if (step === currentStep) return;
    setCurrentStep(step);
  };

  const memoizedFailures = useMemo(() => failures, [failures]);

  // Build a compact summary of changes per dirty store for the confirm modal
  const changeSummary = useMemo(() => {
    if (!fulfillmentType) return [];
    return dirtyStoreIds.map((storeId) => {
      const store = selectedStores.find((s) => s.id === storeId);
      const current = formValuesByStore[storeId];
      const cached = currentValuesCache[storeId];
      const original = fulfillmentType === 'BOPIS'
        ? deriveBopisFormValues(cached)
        : deriveSfsFormValues(cached);

      const changes = [];
      if (current.enabled !== original.enabled) {
        changes.push(`Enabled: ${original.enabled ? 'Yes' : 'No'} → ${current.enabled ? 'Yes' : 'No'}`);
      }
      if (fulfillmentType === 'BOPIS') {
        const cur = current.leadTimeData;
        const orig = original.leadTimeData;
        if (cur.hours !== orig.hours || cur.minutes !== orig.minutes || cur.seconds !== orig.seconds) {
          changes.push(`Lead Time: ${orig.hours}h${orig.minutes}m${orig.seconds}s → ${cur.hours}h${cur.minutes}m${cur.seconds}s`);
        }
      } else {
        daysOfTheWeek.forEach((day) => {
          const curVal = (current.defaultCapacity[day] || { maxUnits: 0 }).maxUnits;
          const origVal = (original.defaultCapacity[day] || { maxUnits: 0 }).maxUnits;
          if (curVal !== origVal) {
            changes.push(`${day.charAt(0).toUpperCase()}${day.slice(1, 3)}: ${origVal} → ${curVal}`);
          }
        });
        const curEx = JSON.stringify(current.capacityExceptions || []);
        const origEx = JSON.stringify(original.capacityExceptions || []);
        if (curEx !== origEx) {
          changes.push(`Capacity Exceptions: ${(original.capacityExceptions || []).length} → ${(current.capacityExceptions || []).length}`);
        }
      }
      return { changes, storeAlias: store ? formatStoreAlias(store) : storeId, storeId };
    });
  }, [dirtyStoreIds, selectedStores, formValuesByStore, currentValuesCache, fulfillmentType]);

  // Deep-clone form values for child use so children can pass the entire row without aliasing.
  const formValuesByStoreClone = useMemo(() => cloneDeep(formValuesByStore), [formValuesByStore]);

  return (
    <section className="o2o-bulk ncss-col-sm-12">
      <Stepper currentStep={currentStep} isLocked={stepperLocked} onNavigate={navigateStep} />

      {currentStep === 1 && (
        <Step1SelectStores
          fulfillmentOption={fulfillmentOption}
          hasCachedValues={hasCachedValues}
          selectedCountry={selectedCountry}
          selectedStores={selectedStores}
          onChangeCountry={onChangeCountry}
          onChangeFulfillmentOption={onChangeFulfillmentOption}
          onChangeSelected={onChangeSelectedStores}
          onNext={onNext}
        />
      )}

      {currentStep === 2 && fulfillmentType && (
        <Step2ConfigureAndSubmit
          allRowsValid={allRowsValid}
          changeSummary={changeSummary}
          currentValuesCache={currentValuesCache}
          dirtyStoreIds={dirtyStoreIds}
          failures={memoizedFailures}
          failuresDownloaded={failuresDownloaded}
          formValuesByStore={formValuesByStoreClone}
          fulfillmentType={fulfillmentType}
          inProgressChunk={inProgressChunk}
          progress={progress}
          selectedStores={selectedStores}
          submitState={submitState}
          onApplyDayToAll={onApplyDayToAll}
          onApplyEnabledToAll={onApplyEnabledToAll}
          onApplyExceptionsToAll={onApplyExceptionsToAll}
          onApplyLeadTimeToAll={onApplyLeadTimeToAll}
          onCacheStore={onCacheStore}
          onCancelSubmit={onCancelSubmit}
          onConfirmSubmit={onConfirmSubmit}
          onDownloadFailures={onDownloadFailures}
          onPrefetchError={onPrefetchError}
          onPrevious={onPrevious}
          onRemoveStore={onRemoveStore}
          onRequestSubmit={onRequestSubmit}
          onResetRow={onResetRow}
          onRetryFailed={onRetryFailed}
          onStartOver={onStartOver}
          onUpdateRowValues={onUpdateRowValues}
        />
      )}
    </section>
  );
};

export default BulkUpdate;
